import { useState } from "react";
import { Mail, MapPin, Phone, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import BrutalButton from "@/components/common/BrutalButton";

const SERVICES = [
    "Software Solutions",
    "SaaS Product",
    "Digital Marketing",
    "Brand & UI",
    "Not sure yet",
];

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        service: SERVICES[0],
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/contact", form);
            setSubmitted(true);
            toast.success("Message sent. We'll be in touch within 48 hours.");
            setForm({ name: "", email: "", company: "", service: SERVICES[0], message: "" });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <section className="border-b-4 border-ink py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-6">
                        <span className="label-mono inline-block bg-brand-yellow border-2 border-ink rounded-full px-3 py-1">
                            Contact
                        </span>
                        <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.92] tracking-tighter">
                            Tell us where{" "}
                            <span className="inline-block bg-brand-green px-3 -rotate-1 border-2 border-ink shadow-brutal">
                                you're stuck.
                            </span>
                        </h1>
                        <p className="mt-7 font-body text-ink/70 text-lg leading-relaxed max-w-xl">
                            We'll send a 1-page plan within 48 hours. No 14-step
                            sales sequence, no “discovery call” theatre.
                        </p>

                        <ul className="mt-10 space-y-4">
                            <li className="flex items-center gap-3 font-body">
                                <span className="w-10 h-10 rounded-lg bg-white border-2 border-ink flex items-center justify-center flex-shrink-0">
                                    <Mail size={18} />
                                </span>
                                <span className="break-all">info@technovalesolutions.com</span>
                            </li>
                            <li className="flex items-center gap-3 font-body">
                                <span className="w-10 h-10 rounded-lg bg-white border-2 border-ink flex items-center justify-center flex-shrink-0">
                                    <Phone size={18} />
                                </span>
                                +91 (931) 074-5117
                            </li>
                            <li className="flex items-center gap-3 font-body">
                                <span className="w-10 h-10 rounded-lg bg-white border-2 border-ink flex items-center justify-center flex-shrink-0">
                                    <MapPin size={18} />
                                </span>
                                188, Sector 48, Gurugram, HR
                            </li>
                        </ul>
                    </div>

                    <div className="lg:col-span-6">
                        <div className="brutal-card bg-white p-7 md:p-9">
                            {submitted ? (
                                <div data-testid="contact-success" className="text-center py-12">
                                    <div className="w-16 h-16 rounded-full bg-brand-green border-2 border-ink flex items-center justify-center mx-auto">
                                        <ArrowRight size={26} />
                                    </div>
                                    <h3 className="mt-6 font-display text-3xl font-black">
                                        Message received.
                                    </h3>
                                    <p className="mt-3 font-body text-ink/70">
                                        We'll reply within 48 hours from info@technovalesolutions.com.
                                    </p>
                                    <BrutalButton
                                        variant="ink"
                                        className="mt-7"
                                        onClick={() => setSubmitted(false)}
                                        data-testid="contact-send-another"
                                    >
                                        Send another
                                    </BrutalButton>
                                </div>
                            ) : (
                                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <Field label="Your name" required>
                                            <input
                                                required
                                                value={form.name}
                                                onChange={update("name")}
                                                className="brutal-input w-full"
                                                placeholder="Lia Park"
                                                data-testid="contact-input-name"
                                            />
                                        </Field>
                                        <Field label="Work email" required>
                                            <input
                                                required
                                                type="email"
                                                value={form.email}
                                                onChange={update("email")}
                                                className="brutal-input w-full"
                                                placeholder="lia@company.com"
                                                data-testid="contact-input-email"
                                            />
                                        </Field>
                                    </div>
                                    <Field label="Company">
                                        <input
                                            value={form.company}
                                            onChange={update("company")}
                                            className="brutal-input w-full"
                                            placeholder="Lattice Hub"
                                            data-testid="contact-input-company"
                                        />
                                    </Field>
                                    <Field label="What do you need?" required>
                                        <select
                                            value={form.service}
                                            onChange={update("service")}
                                            className="brutal-input w-full appearance-none"
                                            data-testid="contact-input-service"
                                        >
                                            {SERVICES.map((s) => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                        </select>
                                    </Field>
                                    <Field label="Tell us about it" required>
                                        <textarea
                                            required
                                            rows={5}
                                            value={form.message}
                                            onChange={update("message")}
                                            className="brutal-input w-full resize-none"
                                            placeholder="We're a Series A SaaS struggling with onboarding conversion..."
                                            data-testid="contact-input-message"
                                        />
                                    </Field>
                                    <BrutalButton
                                        type="submit"
                                        size="lg"
                                        variant="primary"
                                        disabled={submitting}
                                        className="w-full"
                                        data-testid="contact-submit-button"
                                    >
                                        {submitting ? "Sending…" : "Send message"}
                                        <ArrowRight size={18} />
                                    </BrutalButton>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="label-mono mb-2 block">
                {label} {required && <span className="text-brand-orange">*</span>}
            </span>
            {children}
        </label>
    );
}
